/**
 * Clinical plausibility bounds for height/weight/BMI to catch common data-entry
 * mistakes (wrong decimal place, cm vs m, swapped fields). Values are intentionally
 * wide enough for neonates through adults in primary care.
 */

export const CLINICAL_HEIGHT_CM = { min: 22, max: 252 } as const
export const CLINICAL_WEIGHT_KG = { min: 0.25, max: 400 } as const
export const CLINICAL_BMI = { min: 10, max: 80 } as const

const LB_PER_KG = 0.453592
const STONE_PER_KG = 6.35029

export function heightToCm(value: number, unit: 'cm' | 'in'): number {
  return unit === 'in' ? value * 2.54 : value
}

export function weightToKg(value: number, unit: 'kg' | 'lb' | 'stone'): number {
  if (unit === 'lb') return value * LB_PER_KG
  if (unit === 'stone') return value * STONE_PER_KG
  return value
}

function formatHeightUnitBounds(unit: 'cm' | 'in'): string {
  if (unit === 'in') {
    const minIn = Math.ceil((CLINICAL_HEIGHT_CM.min / 2.54) * 10) / 10
    const maxIn = Math.floor((CLINICAL_HEIGHT_CM.max / 2.54) * 10) / 10
    return `${minIn}–${maxIn} in`
  }
  return `${CLINICAL_HEIGHT_CM.min}–${CLINICAL_HEIGHT_CM.max} cm`
}

function formatWeightUnitBounds(unit: 'kg' | 'lb' | 'stone'): string {
  if (unit === 'lb') {
    const minLb = Math.round((CLINICAL_WEIGHT_KG.min / LB_PER_KG) * 10) / 10
    const maxLb = Math.round((CLINICAL_WEIGHT_KG.max / LB_PER_KG) * 10) / 10
    return `${minLb}–${maxLb} lb`
  }
  if (unit === 'stone') {
    const minSt = Math.round((CLINICAL_WEIGHT_KG.min / STONE_PER_KG) * 100) / 100
    const maxSt = Math.round((CLINICAL_WEIGHT_KG.max / STONE_PER_KG) * 10) / 10
    return `${minSt}–${maxSt} st`
  }
  return `${CLINICAL_WEIGHT_KG.min}–${CLINICAL_WEIGHT_KG.max} kg`
}

/**
 * Validate a height field when present (optional field — omit clears validation).
 */
export function validateHeightInput(
  value: number | undefined,
  unit: 'cm' | 'in' | undefined,
): string | null {
  if (value === undefined || value === null || Number.isNaN(value)) return null
  if (value <= 0) {
    return 'Height must be greater than zero.'
  }
  const u = unit || 'cm'
  const cm = heightToCm(value, u)
  if (cm < CLINICAL_HEIGHT_CM.min || cm > CLINICAL_HEIGHT_CM.max) {
    return `Height is outside the usual clinical range (${formatHeightUnitBounds(u)}). Check for a typo or wrong unit.`
  }
  return null
}

/**
 * Validate a weight field when present.
 */
export function validateWeightInput(
  value: number | undefined,
  unit: 'kg' | 'lb' | 'stone' | undefined,
): string | null {
  if (value === undefined || value === null || Number.isNaN(value)) return null
  if (value <= 0) {
    return 'Weight must be greater than zero.'
  }
  const u = unit || 'kg'
  const kg = weightToKg(value, u)
  if (kg < CLINICAL_WEIGHT_KG.min || kg > CLINICAL_WEIGHT_KG.max) {
    return `Weight is outside the usual clinical range (${formatWeightUnitBounds(u)}). Check for a typo or wrong unit.`
  }
  return null
}

function bmiFromCmKg(heightCm: number, weightKg: number): number {
  const heightM = heightCm / 100
  return weightKg / (heightM * heightM)
}

/**
 * When both height and weight are present in SI units, flag impossible BMI pairs
 * (e.g. swapped values, metres entered as cm).
 */
export function validateBmiCombination(heightCm: number, weightKg: number): string | null {
  const bmi = bmiFromCmKg(heightCm, weightKg)
  if (!Number.isFinite(bmi)) return null
  if (bmi < CLINICAL_BMI.min || bmi > CLINICAL_BMI.max) {
    return `Together, height and weight give an unusual BMI (${bmi.toFixed(1)}). Confirm both values are correct.`
  }
  return null
}

export function validateHeightWeightFormPair(args: {
  height?: number
  heightUnit?: 'cm' | 'in'
  weight?: number
  weightUnit?: 'kg' | 'lb' | 'stone'
}): { height: string | null; weight: string | null; combination: string | null } {
  const heightErr = validateHeightInput(args.height, args.heightUnit)
  const weightErr = validateWeightInput(args.weight, args.weightUnit)
  let combination: string | null = null
  if (
    args.height !== undefined &&
    args.weight !== undefined &&
    heightErr === null &&
    weightErr === null &&
    !Number.isNaN(args.height) &&
    !Number.isNaN(args.weight)
  ) {
    const cm = heightToCm(args.height, args.heightUnit || 'cm')
    const kg = weightToKg(args.weight, args.weightUnit || 'kg')
    combination = validateBmiCombination(cm, kg)
  }
  return { height: heightErr, weight: weightErr, combination }
}
